import { useUserStore } from '@/stores/user';
import { httpAPI } from '@/services/ajax';
import app from '@/main.js';
import { defineStore } from 'pinia';
import moment from 'moment';
import { getFreeTrialPersistentNotification } from '@/utils/subscriptionUiConfig';

export const usePermissionStore = defineStore('permission', {
    state: () => ({
        /* IMPORTANT */
        /* Must match exactly what's defined in server Permissions.ts */

        // Perms available to users who do not currently have a subscription plan
        NO_PLAN_PERMS: [
            'team:read',
            'team:write',
            'courses:read',
            'audits:read',
            'library:read',
        ],

        PLAN_LIMITS: {
            null: {
                max_admins: 1, // 1 admin
                max_sites: 1, // 1 site
                max_templates: 0, // ZERO audit templates allowed
                max_ai_messages: 0, // ZERO messages allowed
                max_storage: 0, // ZERO storage allowed
                inventory_enabled: false, // Inventory not available
            },
            TIER_1: {
                max_admins: 1, // 1 admin
                max_sites: 1, // 1 site
                max_templates: 0, // ZERO audit templates allowed
                max_ai_messages: 25, // 25 messages
                max_storage: 0, // ZERO storage allowed
                inventory_enabled: false, // Inventory not available
            },
            TIER_2: {
                max_admins: 2, // 2 admins at most
                max_sites: 2, // 2 sites at most
                max_templates: 5, // 5 audit templates
                max_ai_messages: 100, // 100 messages
                max_storage: 300, // 300 MB
                inventory_enabled: true, // Inventory available (Silver)
            },
            TIER_3: {
                max_admins: 3, // 3 admins at most
                max_sites: 3, // 3 sites at most
                max_templates: null, // no max
                max_ai_messages: 500, // 500 messages
                max_storage: 1000, // 1000 MB (1 GB)
                inventory_enabled: true, // Inventory available (Gold)
            },
        },

        PLAN_NAMES: {
            TIER_1: 'Bronze Plan',
            TIER_2: 'Silver Plan',
            TIER_3: 'Gold Plan',
            null: 'No Active Plan',
        },
    }),
    actions: {
        getCompanyPlanLimit(company, key) {
            const currPlan = company?.last_plan_purchased;

            if (
                currPlan in this.PLAN_LIMITS &&
                key in this.PLAN_LIMITS[currPlan]
            ) {
                return this.PLAN_LIMITS[currPlan][key];
            }

            return null;
        },

        getPlanLimit(key) {
            const userStore = useUserStore();
            const company = userStore.user.company;
            return this.getCompanyPlanLimit(company, key);
        },

        companyHasPermFromAdminOverride(module, company = null) {
            /* Does the company have the provided permission via a plan override from an app admin? */
            if (!company) {
                const userStore = useUserStore();
                company = userStore.user?.company;
            }
            return company.override_permissions_json?.includes(module);
        },

        companyHasPermFromPlan(module, company = null) {
            /* Does the company have the provided permission via their current subscription plan? */

            if (!company) {
                const userStore = useUserStore();
                company = userStore.user?.company;
            }

            // Only available via admin override
            if (
                module === 'alpha:access' ||
                module === 'secondments:read' ||
                module === 'secondments:write'
            ) {
                return false;
            }

            const hasActivePlan = this.isCompanyPlanActive(company);

            // Special handling for inventory permissions - check tier configuration
            if (module === 'inventory:read' || module === 'inventory:write') {
                const inventoryEnabled = this.getCompanyPlanLimit(
                    company,
                    'inventory_enabled'
                );

                // inventory:read is available to all users in Silver/Gold companies (like a NO_PLAN_PERM)
                if (module === 'inventory:read') {
                    return inventoryEnabled === true;
                }

                // inventory:write requires active plan AND inventory enabled (only for admins by default)
                return hasActivePlan && inventoryEnabled === true;
            }

            return hasActivePlan || this.NO_PLAN_PERMS.includes(module);
        },

        companyHasPerm(module, company = null) {
            if (!company) {
                const userStore = useUserStore();
                company = userStore.user?.company;
            }

            return (
                this.companyHasPermFromPlan(module, company) ||
                this.companyHasPermFromAdminOverride(module, company)
            );
        },

        companyHasPermOneOf(...modules) {
            if (
                this.isPlanActive ||
                modules.some((m) => this.NO_PLAN_PERMS.includes(m))
            ) {
                return true;
            }
            const userStore = useUserStore();
            return modules.some((module) =>
                userStore.user?.company?.override_permissions_json?.includes(
                    module
                )
            );
        },

        hasPerm(module) {
            const userStore = useUserStore();
            return (
                userStore.isAppAdmin ||
                (userStore.companyHasPerm(module) &&
                    (userStore.isCompanyAdmin ||
                        userStore.user?.permissions_json?.includes(module)))
            );
        },

        hasPermOneOf(...modules) {
            const userStore = useUserStore();
            return (
                userStore.isAppAdmin ||
                modules.some(
                    (module) =>
                        userStore.companyHasPerm(module) &&
                        (userStore.isCompanyAdmin ||
                            userStore.user?.permissions_json?.includes(module))
                )
            );
        },

        isCompanyPlanActive(company) {
            /* Does the user's company have an active subscription? */

            if (
                !company ||
                !company.last_plan_purchased ||
                !company.last_plan_purchased_starts_at
            )
                return false;

            const now = new Date();
            const startsAt = new Date(company.last_plan_purchased_starts_at);

            // If no end date, treat as active (indefinite plan)
            if (!company.last_plan_purchased_ends_at) {
                return startsAt <= now;
            }

            const endsAt = new Date(company.last_plan_purchased_ends_at);
            return startsAt <= now && now < endsAt;
        },

        isCompanyFreeTrial(company) {
            // A company is in a free trial IF:
            // 1. The trial has started (start date <= now)
            // 2. The trial hasn't ended (trial end date > now)
            // 3. The last plan purchased end date matches the trial end date
            if (
                !company ||
                !company.last_plan_purchased ||
                !company.last_plan_purchased_starts_at ||
                !company.last_plan_purchased_ends_at ||
                !company.last_free_trial_ends_at
            )
                return false;

            const now = moment();
            const planStartsAt = moment(company.last_plan_purchased_starts_at);
            const trialEndDate = moment(company.last_free_trial_ends_at);
            const planEndsAt = moment(company.last_plan_purchased_ends_at);

            // Check that trial has started, hasn't ended, and plan end matches trial end
            return (
                planStartsAt.isSameOrBefore(now) &&
                trialEndDate.isAfter(now) &&
                planEndsAt.isSame(trialEndDate, 'day') // Compare dates
            );
        },

        getCompanyPlanName(company) {
            return this.PLAN_NAMES[company.last_plan_purchased];
        },
    },
    getters: {
        isPlanActive(state) {
            /* Does the user's company have an active subscription? */
            const userStore = useUserStore();
            return userStore.isSubscriptionActive;
        },

        getPlanName(state) {
            const userStore = useUserStore();
            const company = userStore.user?.company;
            return this.getCompanyPlanName(company);
        },

        freeTrialEndDateString(state) {
            const userStore = useUserStore();
            const freeTrialEndDate =
                userStore.user?.company?.last_free_trial_ends_at;
            if (!freeTrialEndDate) {
                return null;
            }
            return moment(freeTrialEndDate).format('MMMM D, YYYY h:mm A');
        },

        freeTrialPersistentNotification(state) {
            const userStore = useUserStore();
            return getFreeTrialPersistentNotification({
                subscriptionDetails: userStore.subscriptionDetails,
                isCompanyAdmin: userStore.isCompanyAdmin,
                freeTrialEndDateString: this.freeTrialEndDateString,
                planName: this.getPlanName,
            });
        },
    },
});
